import {
  ApiError,
  type AuthenticatedTransport,
  requestJson,
  requireStatus,
} from "@groundspark/browser-auth";
import {
  type Branch,
  type Communication,
  type CommunicationDraft,
  type CommunicationDelivery,
  type DraftContent,
  type Channel,
  type Leaflet,
  type LeafletDocument,
  type LeafletScans,
  type Member,
  type Page,
  identifier,
  parseBranch,
  parseCommunication,
  parseDraft,
  parseDelivery,
  parseLeaflet,
  parseLeafletDocument,
  parseLeafletScans,
  parseList,
  parseMember,
  parsePage,
} from "./domain";
import { bitmapDigest, isBitmapType } from "./leaflet-bitmaps";
import type { Pages, PaperSize } from "./leaflet-pages";
import { type BranchStatistics, parseStatistics } from "./statistics";

/** Leaflet pages and bitmaps can be megabytes. */
const LEAFLET_TRANSFER_TIMEOUT = 120_000;

export class BranchApi {
  public constructor(private readonly authentication: AuthenticatedTransport) {}

  public listBranches(signal?: AbortSignal): Promise<readonly Branch[]> {
    return requestJson(
      this.authentication,
      "/v1/branches",
      (value) => parseList(value, parseBranch),
      "branches",
      { signal },
    );
  }

  public statistics(branchId: string, signal?: AbortSignal): Promise<BranchStatistics> {
    return requestJson(
      this.authentication,
      `/v1/branches/${encodeURIComponent(identifier(branchId))}/statistics`,
      parseStatistics,
      "statistics",
      { signal },
    );
  }

  public listMembers(
    branchId: string,
    cursor: string | null = null,
    signal?: AbortSignal,
  ): Promise<Page<Member>> {
    return this.page(branchId, "members", cursor, parseMember, signal);
  }

  public listCommunications(
    branchId: string,
    cursor: string | null = null,
    signal?: AbortSignal,
  ): Promise<Page<Communication>> {
    return this.page(branchId, "communications", cursor, parseCommunication, signal);
  }

  public createDraft(
    branchId: string,
    channel: Channel,
    signal?: AbortSignal,
  ): Promise<CommunicationDraft> {
    return requestJson(this.authentication, this.draftPath(branchId), parseDraft, "draft", {
      method: "POST",
      body: JSON.stringify({ channel }),
      signal,
    });
  }

  public getDraft(
    branchId: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<CommunicationDraft> {
    return requestJson(this.authentication, this.draftPath(branchId, id), parseDraft, "draft", {
      signal,
    });
  }

  public saveDraft(
    branchId: string,
    id: string,
    revision: number,
    content: DraftContent,
  ): Promise<CommunicationDraft> {
    return requestJson(this.authentication, this.draftPath(branchId, id), parseDraft, "draft", {
      method: "PUT",
      body: JSON.stringify({
        expected_revision: revision,
        title: content.title,
        preview_text: content.previewText,
        body: content.body,
        recipient_ids: content.recipientIds,
      }),
    });
  }

  public async deleteDraft(
    branchId: string,
    id: string,
    revision: number,
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await this.authentication.authenticatedFetch(
      this.draftPath(branchId, id),
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ expected_revision: revision }),
        signal,
      },
    );
    await requireStatus(response, 204, signal);
  }

  private draftPath(branchId: string, id?: string): string {
    const path = `/v1/branches/${encodeURIComponent(identifier(branchId))}/communications`;
    return id === undefined ? path : `${path}/${encodeURIComponent(identifier(id))}`;
  }

  public submit(branchId: string, id: string, revision: number): Promise<Communication> {
    return requestJson(
      this.authentication,
      `${this.draftPath(branchId, id)}/submit`,
      parseCommunication,
      "communication",
      {
        method: "POST",
        body: JSON.stringify({ expected_revision: revision }),
      },
    );
  }

  public deliveries(
    branchId: string,
    id: string,
    cursor: string | null,
    signal?: AbortSignal,
  ): Promise<Page<CommunicationDelivery>> {
    const query = new URLSearchParams({ limit: "100" });
    if (cursor !== null) query.set("cursor", identifier(cursor));
    return requestJson(
      this.authentication,
      `${this.draftPath(branchId, id)}/deliveries?${query}`,
      (value) => parsePage(value, parseDelivery),
      "delivery statuses",
      { signal },
    );
  }

  public listLeaflets(
    branchId: string,
    cursor: string | null = null,
    signal?: AbortSignal,
  ): Promise<Page<Leaflet>> {
    return this.page(branchId, "leaflets", cursor, parseLeaflet, signal);
  }

  /** `destination` is where scanning the leaflet's QR code leads. */
  public createLeaflet(
    branchId: string,
    name: string,
    paperSize: PaperSize,
    destination: string,
    signal?: AbortSignal,
  ): Promise<Leaflet> {
    return requestJson(
      this.authentication,
      this.leafletPath(branchId),
      parseLeaflet,
      "leaflet",
      {
        method: "POST",
        body: JSON.stringify({ name, paper_size: paperSize, qr_code: { destination } }),
        signal,
      },
    );
  }

  /** Changes where the QR code leads; printed codes follow the change. */
  public setLeafletQrDestination(
    branchId: string,
    id: string,
    destination: string,
    signal?: AbortSignal,
  ): Promise<Leaflet> {
    return requestJson(
      this.authentication,
      `${this.leafletPath(branchId, id)}/qr-code`,
      parseLeaflet,
      "leaflet",
      { method: "PUT", body: JSON.stringify({ destination }), signal },
    );
  }

  public leafletScans(
    branchId: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<LeafletScans> {
    return requestJson(
      this.authentication,
      `${this.leafletPath(branchId, id)}/scans`,
      parseLeafletScans,
      "leaflet scans",
      { signal },
    );
  }

  public getLeaflet(
    branchId: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<LeafletDocument> {
    return requestJson(
      this.authentication,
      this.leafletPath(branchId, id),
      parseLeafletDocument,
      "leaflet",
      { signal, timeout: LEAFLET_TRANSFER_TIMEOUT },
    );
  }

  public saveLeaflet(
    branchId: string,
    id: string,
    revision: number,
    pages: Pages,
  ): Promise<Leaflet> {
    return requestJson(
      this.authentication,
      this.leafletPath(branchId, id),
      parseLeaflet,
      "leaflet",
      {
        method: "PUT",
        body: JSON.stringify({
          expected_revision: revision,
          pages: { front: pages.front, back: pages.back },
        }),
        timeout: LEAFLET_TRANSFER_TIMEOUT,
      },
    );
  }

  public async deleteLeaflet(
    branchId: string,
    id: string,
    revision: number,
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await this.authentication.authenticatedFetch(
      this.leafletPath(branchId, id),
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ expected_revision: revision }),
        signal,
      },
    );
    await requireStatus(response, 204, signal);
  }

  /** Stores a bitmap for the branch's leaflets; storing it again changes nothing. */
  public async uploadLeafletBitmap(
    branchId: string,
    reference: string,
    bitmap: Blob,
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await this.authentication.authenticatedFetch(
      this.bitmapPath(branchId, reference),
      {
        method: "PUT",
        headers: { "Content-Type": bitmap.type },
        body: bitmap,
        signal,
        timeout: LEAFLET_TRANSFER_TIMEOUT,
      },
    );
    await requireStatus(response, 204, signal);
  }

  public async leafletBitmap(
    branchId: string,
    reference: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    const response = await this.authentication.authenticatedFetch(
      this.bitmapPath(branchId, reference),
      { signal, timeout: LEAFLET_TRANSFER_TIMEOUT },
    );
    await requireStatus(response, 200, signal);
    const type = response.headers.get("Content-Type");
    if (!isBitmapType(type))
      throw new ApiError("The server returned an unsupported image.", response.status);
    return new Blob([await response.arrayBuffer()], { type });
  }

  private bitmapPath(branchId: string, reference: string): string {
    return `/v1/branches/${encodeURIComponent(identifier(branchId))}/leaflet-bitmaps/${bitmapDigest(reference)}`;
  }

  private leafletPath(branchId: string, id?: string): string {
    const path = `/v1/branches/${encodeURIComponent(identifier(branchId))}/leaflets`;
    return id === undefined ? path : `${path}/${encodeURIComponent(identifier(id))}`;
  }

  private page<T>(
    branchId: string,
    resource: string,
    cursor: string | null,
    parser: (value: unknown) => T,
    signal?: AbortSignal,
  ): Promise<Page<T>> {
    const query = new URLSearchParams({ limit: "100" });
    if (cursor !== null) query.set("cursor", identifier(cursor));
    return requestJson(
      this.authentication,
      `/v1/branches/${encodeURIComponent(identifier(branchId))}/${resource}?${query}`,
      (value) => parsePage(value, parser),
      resource,
      { signal },
    );
  }
}
